// Built-ins
import { useState, useEffect, useCallback } from "react";

// Icons / Images
import { BiPlus } from "react-icons/bi";
import { AiOutlineWarning, AiOutlineCheckCircle } from "react-icons/ai";

import axios from "axios";

// Modules / Functions
import Swal from "sweetalert2";
import { Modal, Col, Form, Row } from "react-bootstrap";
import GuardedModal from "../../common/GuardedModal";
import { Button } from "semantic-ui-react";

// Global Variables
import { headers } from "../../global_vars";
import { CASH_TYPE_FIELD_LABEL } from "../../common/relatedLinkLabels";
import AutocompleteSelect from "../../common/AutocompleteSelect";
import SelectWithInlineCreate from "../../common/SelectWithInlineCreate";
import AddCountryModal from "./add_country";
import DateInput from "../../common/DateInput";


// Variables
window.Swal = Swal;

// API endpoint
const ADD_CASH = "https://ultima.icsgr.com/api/data_management/cash/";

function AddCashModal({
  onCashCreated,
  refreshData,
  defaultProjectId,
  lockProject = false,
  defaultConsultantId,
  lockConsultant = false,
  projectOptions,
}) {
  const [show, setShow] = useState(false);

  // Basic Information
  const [project, setProject] = useState("");
  const [country, setCountry] = useState("");
  const [trandate, setTrandate] = useState("");
  const [consultant, setConsultant] = useState("");
  const [kind, setKind] = useState("E"); // Default to Debit (API: E)
  const [amountexp, setAmountexp] = useState("");
  const [amountpay, setAmountpay] = useState("");
  const [reason, setReason] = useState("");

  // Dropdown Data
  const [projects, setProjects] = useState([]);
  const [countries, setCountries] = useState([]);
  const [consultants, setConsultants] = useState([]);
  const [addCountryOpen, setAddCountryOpen] = useState(false);

  const resetForm = () => {
    setProject("");
    setCountry("");
    setTrandate("");
    setConsultant("");
    setKind("E");
    setAmountexp("");
    setAmountpay("");
    setReason("");
  };

  const handleClose = () => setShow(false);
  const handleShow = () => {
    resetForm();
    if (defaultProjectId) setProject(defaultProjectId);
    if (defaultConsultantId) setConsultant(defaultConsultantId);
    setShow(true);
  };

  const loadDropdownData = useCallback(async () => {
    try {
      const currentHeaders = {
        ...headers,
        "Authorization": "Token " + localStorage.getItem("userToken")
      };

      const countriesRes = await axios.get("https://ultima.icsgr.com/api/regions/all_countries/", { headers: currentHeaders });
      const consultantsRes = await axios.get("https://ultima.icsgr.com/api/administration/all_consultants/", { headers: currentHeaders });

      let projectsData = [];
      if (Array.isArray(projectOptions)) {
        projectsData = projectOptions;
      } else {
        const projectsRes = await axios.get("https://ultima.icsgr.com/api/data_management/all_projects/", { headers: currentHeaders });
        projectsData = projectsRes?.data?.all_projects || [];
      }

      const countriesData = countriesRes?.data?.all_countries || [];
      const consultantsData = consultantsRes?.data?.all_consultants || [];

      setProjects(projectsData);
      setCountries(countriesData);
      setConsultants(consultantsData);
    } catch (error) {
      console.error('Error loading dropdown data:', error);
      setProjects([]);
      setCountries([]);
      setConsultants([]);
    }
  }, [projectOptions]);

  // Load dropdown data when modal opens
  useEffect(() => {
    if (show) {
      loadDropdownData();
    }
  }, [show, loadDropdownData]);

  // Clear the irrelevant amount field when kind changes
  useEffect(() => {
    if (kind === "E") {
      setAmountpay("");
    } else if (kind === "P") {
      setAmountexp("");
    }
  }, [kind]);

  // Validation
  const isProjectValid = project.length > 0;
  const isCountryValid = country.length > 0;
  const isTrandateValid = trandate.length > 0;
  const isConsultantValid = consultant.length > 0;
  const isReasonValid = reason.trim().length >= 1 && reason.trim().length <= 120;

  const isFormValid = isProjectValid && isCountryValid && isTrandateValid && isConsultantValid && isReasonValid;

  const createNewCash = async () => {
    try {
      // Update headers with current token
      const currentHeaders = {
        ...headers,
        "Authorization": "Token " + localStorage.getItem("userToken")
      };

      await axios({
        method: "post",
        url: ADD_CASH,
        headers: currentHeaders,
        data: {
          // Required fields
          project_id: project,
          country_id: country,
          trandate: trandate,
          consultant_id: consultant,
          kind: kind,

          // Optional fields
          amountexp: kind === 'E' ? (amountexp.trim() || null) : null,
          amountpay: kind === 'P' ? (amountpay.trim() || null) : null,
          reason: reason.trim() || null,
        },
      });

      if (refreshData) {
        setShow(false);
        refreshData();
      } else {
        // legacy behavior: navigate to overview
        // Note: Since ID is now auto-generated, we navigate to the list instead
        window.location.href = `/data_management/cash/`;
      }
    } catch (e) {
      console.error('Cash creation error:', e.response?.data);
      const apiMsg =
        e?.response?.data?.errormsg ||
        e?.response?.data?.detail ||
        e?.response?.data ||
        "Something went wrong while creating the accounting entry.";
      Swal.fire({
        icon: "error",
        title: "Error",
        text: typeof apiMsg === 'object' ? JSON.stringify(apiMsg) : apiMsg,
      });
    }
  };

  return (
    <>
      <Button color="green" style={{ margin: 20 }} onClick={handleShow}>
        <BiPlus
          style={{ color: "white", fontSize: "1.3em", marginRight: "0.3em" }}
        />
        Create new Accounting entry
      </Button>

      <GuardedModal
        show={show}
        size="lg"
        onHide={handleClose}
        aria-labelledby="contained-modal-title-vcenter"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Create new Accounting entry</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <Row className="justify-content-md-center">
            <Col>
              <Form>
                {/* Basic information */}
                <h6 className="mb-3">Basic information</h6>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Project *:</Form.Label>
                      <AutocompleteSelect
                        value={project}
                        onChange={setProject}
                        disabled={lockProject}
                        placeholder="Select project"
                      >
                        {Array.isArray(projects) && projects.map((project) => (
                          <option key={project.project_id} value={project.project_id}>
                            {project.title}
                          </option>
                        ))}
                      </AutocompleteSelect>
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col md={6}>
                    <SelectWithInlineCreate
                      className="mb-3"
                      label="Country"
                      required
                      value={country}
                      onChange={setCountry}
                      placeholderOption="Select country"
                      onAddClick={() => setAddCountryOpen(true)}
                      overviewLinkEntity="country"
                    >
                      {Array.isArray(countries) && countries.map((c) => (
                        <option key={c.country_id} value={c.country_id} title={c.country_id}>
                          {c.title}
                        </option>
                      ))}
                    </SelectWithInlineCreate>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Transaction date *:</Form.Label>
                      <DateInput
                        onChange={(e) => setTrandate(e.target.value)}
                        value={trandate}
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Consultant *:</Form.Label>
                      <AutocompleteSelect
                        value={consultant}
                        onChange={setConsultant}
                        disabled={lockConsultant}
                        placeholder="Select consultant"
                      >
                        {Array.isArray(consultants) && consultants.map((consultant) => (
                          <option key={consultant.consultant_id} value={consultant.consultant_id}>
                            {consultant.fullname}
                          </option>
                        ))}
                      </AutocompleteSelect>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>{CASH_TYPE_FIELD_LABEL}:</Form.Label>
                      <AutocompleteSelect value={kind} onChange={setKind}>
                        <option value="E">Debit</option>
                        <option value="P">Credit</option>
                      </AutocompleteSelect>
                    </Form.Group>
                  </Col>
                </Row>

                {/* Currency field removed; currency derives from selected Country */}

                {kind === 'E' && (
                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Amount expense:</Form.Label>
                        <Form.Control
                          type="number"
                          placeholder="e.g., 100.00"
                          onChange={(e) => setAmountexp(e.target.value)}
                          value={amountexp}
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                )}
                {kind === 'P' && (
                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Amount payment:</Form.Label>
                        <Form.Control
                          type="number"
                          placeholder="e.g., 100.00"
                          onChange={(e) => setAmountpay(e.target.value)}
                          value={amountpay}
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                )}

                <Form.Group className="mb-3">
                  <Form.Label>Reason *:</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    placeholder="Reason for the accounting entry"
                    onChange={(e) => setReason(e.target.value)}
                    value={reason}
                  />
                </Form.Group>
              </Form>
            </Col>
          </Row>
        </Modal.Body>

        <Modal.Footer>
          <small className="mr-auto">
            {!isFormValid ? (
              <ul
                className="mr-auto"
                style={{ margin: 0, padding: 0, color: "red" }}
              >
                {!isProjectValid && (
                  <li>
                    <AiOutlineWarning style={{ fontSize: 18, marginRight: 6 }} />
                    Project is required.
                  </li>
                )}
                {!isCountryValid && (
                  <li>
                    <AiOutlineWarning style={{ fontSize: 18, marginRight: 6 }} />
                    Country is required.
                  </li>
                )}
                {!isTrandateValid && (
                  <li>
                    <AiOutlineWarning style={{ fontSize: 18, marginRight: 6 }} />
                    Transaction date is required.
                  </li>
                )}
                {!isConsultantValid && (
                  <li>
                    <AiOutlineWarning style={{ fontSize: 18, marginRight: 6 }} />
                    Consultant is required.
                  </li>
                )}
                {!isReasonValid && (
                  <li>
                    <AiOutlineWarning style={{ fontSize: 18, marginRight: 6 }} />
                    Reason is required (1–120 chars).
                  </li>
                )}
              </ul>
            ) : (
              <ul
                className="mr-auto"
                style={{ margin: 0, padding: 0, color: "green" }}
              >
                <li>
                  <AiOutlineCheckCircle
                    style={{ fontSize: 18, marginRight: 6 }}
                  />
                  Validated
                </li>
              </ul>
            )}
          </small>

          <Button color="red" onClick={handleClose}>
            Close
          </Button>
          <Button
            color="green"
            onClick={() => {
              handleClose();
              createNewCash();
            }}
            disabled={!isFormValid}
          >
            Save Changes
          </Button>
        </Modal.Footer>
      </GuardedModal>

      <AddCountryModal
        nested
        hideTrigger
        show={addCountryOpen}
        onHide={() => setAddCountryOpen(false)}
        onCountryCreated={async (data) => {
          await loadDropdownData();
          const id = data?.country_id;
          if (id) setCountry(id);
          setAddCountryOpen(false);
        }}
      />
    </>
  );
}

export default AddCashModal;
